import { ChevronRightIcon } from '@heroicons/react/24/outline';
import type { ColumnType } from 'antd/es/table';

import { formatDecimalAmount } from 'utils/formatters/formatCurrency';
import { hasValue } from 'utils/hasValue';

import type { PayoutBatches } from 'api';

import { ColumnTitle } from '@atoms/ColumnTitle';
import { BatchesIcon } from '@atoms/Icon/BatchesIcon';
import { PaperClipIcon } from '@atoms/Icon/PaperClipIcon';
import { StatusTag } from '@atoms/StatusTag';

import { FormattedDateTime } from '@molecules/FormattedDateTime';

import { BATCHES_STATUSES } from './constants';

export const getColumns = (
  isBatchApprovalRequired: boolean
): ColumnType<PayoutBatches>[] => {
  return [
    {
      dataIndex: 'icon',
      key: 'icon',
      width: 100,
      render: () => (
        <div className="flex justify-center">
          <BatchesIcon width={32} height={32} />
        </div>
      )
    },
    {
      title: <ColumnTitle title="Date" />,
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt: string) => {
        return <FormattedDateTime value={createdAt} />;
      }
    },
    {
      title: <ColumnTitle title="Created by" />,
      dataIndex: 'creator_name',
      key: 'creator_name',
      render: (creatorName: string) => (
        <div className="font-semibold">{creatorName}</div>
      )
    },
    {
      title: <ColumnTitle title="Name" />,
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => {
        return <div className="text-balance">{name}</div>;
      }
    },
    {
      title: <ColumnTitle title="Country" />,
      dataIndex: 'country.name',
      key: 'country',
      render: (_: string, record: PayoutBatches) => (
        <div className="font-semibold">{record.country?.name}</div>
      )
    },
    {
      title: <ColumnTitle title="Payouts" />,
      dataIndex: 'total_payout_requests_count',
      key: 'total_payout_requests_count',
      render: (totalPayoutRequestsCount: number) => (
        <div className="font-semibold">{totalPayoutRequestsCount}</div>
      )
    },
    {
      title: <ColumnTitle title="Amount" />,
      dataIndex: 'total_amount',
      key: 'total_amount',
      align: isBatchApprovalRequired ? 'left' : 'right',
      render: (totalAmount: number | null, record: PayoutBatches) => {
        const formattedAmount = hasValue(totalAmount)
          ? formatDecimalAmount(totalAmount)
          : 'Total amount unavailable';

        return (
          <div>
            {record.currency?.iso}
            <br />
            <div className="font-semibold">{formattedAmount}</div>
          </div>
        );
      }
    },
    {
      title: <ColumnTitle title="Status" />,
      dataIndex: 'status',
      key: 'status',
      align: 'right',
      className: '!pr-4',
      render: (status: string) => {
        return <StatusTag status={status} statuses={BATCHES_STATUSES} />;
      },
      hidden: !isBatchApprovalRequired
    },
    {
      dataIndex: 'attachments',
      key: 'paperClipIcon',
      align: 'right',
      width: 30,
      render: (_: unknown, record: PayoutBatches) => {
        if (record.attachments_count > 0) {
          return <PaperClipIcon className="h-9 stroke-content-subtle pt-3" />;
        }
        return null;
      }
    },
    {
      dataIndex: 'icon',
      key: 'arrowIcon',
      align: 'right',
      width: 50,
      render: () => (
        <ChevronRightIcon className="h-9 stroke-content-subtle pt-3" />
      )
    }
  ];
};
